import { ChevronDown, ChevronRight, Paperclip, Plus, X } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { toast } from 'sonner';
import { useSession } from '@/components/auth/session';
import { useShell } from '@/components/layout/shell';
import { Button } from '@/components/ui/button';
import { Combobox } from '@/components/ui/combobox';
import { Input, Textarea } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Modal } from '@/components/ui/overlay';
import { PLAN_QUEUED_NOTE } from '@/features/actions/verbs';
import { useRepos } from '@/hooks/use-fleet';
import { useHubAction } from '@/hooks/use-hub-action';
import { api } from '@/lib/api';
import { formatBytes } from '@/lib/format';

/**
 * Vocabulary note. The console calls the thing a person opens a **job**; the
 * hub, the API, the database and the CLI still call it a brief, and the code in
 * this file keeps the hub's word so it reads against `api.fileBrief` and the
 * `ProjectBrief` schema without translation. Only the copy says "job". Changing
 * the wire name would break `/api/briefs`, the MCP server and every worker.
 */

/**
 * What the hub accepts, mirrored from `ProjectBriefInput` in core: 50
 * constraints, 500 characters each. Mirrored rather than imported for the same
 * reason the status lists are, and worth mirroring at all because the
 * alternative is a raw zod path in a toast.
 */
const MAX_CONSTRAINTS = 50;
const MAX_CONSTRAINT_LENGTH = 500;

/**
 * What the hub will take as an attachment, mirrored from `ATTACHMENT_MAX_BYTES`,
 * `ATTACHMENTS_MAX_BYTES_PER_BRIEF`, `ATTACHMENTS_MAX_PER_BRIEF` and
 * `ATTACHMENT_MEDIA_TYPES` in core. Mirrored rather than imported for the same
 * reason the status lists and the constraint caps above are: importing a core
 * value pulls `node:crypto` into this bundle. Worth mirroring at all because
 * the alternative is a person watching a 40 MB video upload for a minute to
 * find out the hub was never going to keep it. `job-files.test.ts` pins the
 * four against core so they cannot drift.
 */
const MAX_FILES = 10;
const MAX_FILE_BYTES = 10_000_000;
const MAX_FILES_BYTES = 25_000_000;
const FILE_TYPES: readonly string[] = [
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
  'application/pdf',
  'text/plain',
  'text/markdown',
];

/**
 * Whether the hub takes this file's type, mirroring `isAllowedAttachmentType`:
 * a browser sends `text/plain; charset=utf-8` and case is not significant, so
 * the parameters are cut and the comparison is lower-cased. A file the browser
 * declared no type for at all is refused here, because the hub refuses it too.
 */
function allowedType(mediaType: string): boolean {
  const essence = mediaType.split(';')[0]?.trim().toLowerCase() ?? '';
  return FILE_TYPES.includes(essence);
}

/** The textarea as the hub reads it: one trimmed constraint per non-empty line. */
function constraintLines(text: string): string[] {
  return text
    .split('\n')
    .map((line) => line.trim())
    .filter(Boolean);
}

/**
 * Why the hub would refuse these rules, in the reader's own terms.
 *
 * The caps are the schema's, and without this a person who pasted a paragraph
 * gets `constraints.0: Too big: expected string to have <=500 characters` in a
 * red toast, which names a field they never typed and a rule nobody told them.
 */
function constraintProblem(lines: readonly string[]): string | null {
  if (lines.length > MAX_CONSTRAINTS) {
    return `That is ${lines.length} rules; the hub takes ${MAX_CONSTRAINTS}. Fold some together or move the detail into the description.`;
  }
  const long = lines.findIndex((line) => line.length > MAX_CONSTRAINT_LENGTH);
  if (long >= 0) {
    return `Rule ${long + 1} is ${lines[long]!.length} characters; the hub takes ${MAX_CONSTRAINT_LENGTH}. It probably belongs in the description.`;
  }
  return null;
}

/**
 * Why the hub would refuse these files, in the reader's own terms, the same job
 * `constraintProblem` does for the rules.
 *
 * Checked before anything is sent, and in the order the hub checks it, so the
 * sentence a person reads here is the sentence they would have read after the
 * upload. Each one names the limit that was passed, because a refusal that does
 * not say what would have worked leaves them guessing.
 */
function filesProblem(files: readonly File[]): string | null {
  if (files.length > MAX_FILES) {
    return `That is ${files.length} files; the hub takes ${MAX_FILES} on a job. Remove some, or open a second job for the rest.`;
  }
  const big = files.find((file) => file.size > MAX_FILE_BYTES);
  if (big) {
    return `${big.name} is ${formatBytes(big.size)}; the hub takes ${formatBytes(MAX_FILE_BYTES)} per file. Crop the screenshot or link to it instead.`;
  }
  const total = files.reduce((sum, file) => sum + file.size, 0);
  if (total > MAX_FILES_BYTES) {
    return `Those files come to ${formatBytes(total)}; the hub takes ${formatBytes(MAX_FILES_BYTES)} on one job. Remove one.`;
  }
  const wrong = files.find((file) => !allowedType(file.type));
  if (wrong) {
    return `${wrong.name} is ${wrong.type || 'of no declared type'}; the hub takes ${FILE_TYPES.join(', ')}.`;
  }
  return null;
}

/**
 * A date field's `YYYY-MM-DD` as the instant the job is due.
 *
 * The end of that day rather than its start, in the reader's own timezone,
 * because "by Friday" means the end of Friday where the person typing it is
 * sitting. An empty or unparseable field is no deadline at all rather than a
 * guess: the column is nullable and a wrong date is worse than none.
 */
function isoDeadline(value: string): string | null {
  if (!value) return null;
  const parsed = new Date(`${value}T23:59:59`);
  return Number.isNaN(parsed.getTime()) ? null : parsed.toISOString();
}

/**
 * Opening a job, over whatever page is open (issue #89).
 *
 * This was a panel that expanded at the top of the Jobs list, the way a reply
 * box expands in a thread: right while the composer could only ever be reached
 * from Jobs, wrong once `c`, the header button and the palette could all open
 * it from anywhere else in the console, because "compose" then quietly meant
 * "navigate to Jobs and lose whatever you were reading". Now it is a modal
 * over the current page instead - `Modal` from `components/ui/overlay`, the
 * one shared with `SplitPane`'s own floating pane and the verb popovers this
 * issue also adds - so opening it never costs the page underneath, and
 * closing it (Escape, the scrim, or Cancel) returns to exactly that page.
 *
 * It renders once, at the root (`routes/__root.tsx`), rather than on the Jobs
 * route: that is what makes a draft survive being closed and reopened in the
 * same session - the component itself, and the `useState` hooks its fields
 * live in, never unmount when the person navigates or dismisses the modal,
 * only the dialog's own visibility does.
 *
 * Four fields are always visible: the title, what should be done, the repo,
 * and the files to attach. The first three are what the hub requires and what a
 * person always knows; the fourth is there because a screenshot of the thing
 * that is wrong is usually the clearest half of the description, and a field
 * behind a toggle is a field nobody uses.
 *
 * Rules and the due date are real fields on the job and the planner is
 * handed both, but most jobs have neither, so they sit behind "More options"
 * rather than making every job look like a form with five blanks to fill.
 *
 * It opens from the header button, from `c`, and from the command palette --
 * which is why the open flag lives in the shell rather than here.
 */
export function BriefComposer() {
  const { composeOpen, closeCompose } = useShell();
  const { session } = useSession();
  const [title, setTitle] = useState('');
  const [goal, setGoal] = useState('');
  const [repos, setRepos] = useState<string[]>([]);
  // Beside the text rather than inside the modal's own lifetime, so a draft that
  // is closed and reopened still has its screenshot on it: this component never
  // unmounts (see the doc comment above), and its files should keep the same
  // promise its fields do.
  const [files, setFiles] = useState<File[]>([]);
  const [constraints, setConstraints] = useState('');
  const [deadline, setDeadline] = useState('');
  const [moreOpen, setMoreOpen] = useState(false);
  const first = useRef<HTMLInputElement>(null);
  // The registry (issue #101), not the free-text history `useProjects` reads:
  // the field this feeds is a picker now, gated to what an admin has actually
  // registered and left enabled, so a typo cannot mint a phantom repo any
  // more than a picker over a dropdown can.
  const registry = useRepos();
  const known = (registry.data ?? []).filter((repo) => repo.enabled).map((repo) => repo.slug);
  const lines = constraintLines(constraints);
  const tooMuch = constraintProblem(lines);
  const badFiles = filesProblem(files);

  /**
   * Add what was picked or dropped to what is already pending, rather than
   * replacing it: a person who drops two screenshots and then picks a PDF means
   * all three. The same file twice - dropped, then picked again - is one file,
   * matched on name and size, because the hub would store it twice and the
   * agent would read the same picture twice.
   */
  const addFiles = (picked: FileList | null) => {
    if (!picked?.length) return;
    setFiles((current) => {
      const next = [...current];
      for (const file of Array.from(picked)) {
        const already = next.some(
          (existing) => existing.name === file.name && existing.size === file.size,
        );
        if (!already) next.push(file);
      }
      return next;
    });
  };
  // Something typed into the optional fields must stay visible even after the
  // toggle is folded, or a rule the person forgot they wrote goes to the planner
  // unseen.
  const hasDetails = lines.length > 0 || deadline !== '';
  const showMore = moreOpen || hasDetails;

  const open = useHubAction(
    async () => {
      const filed = await api.fileBrief({
        title: title.trim(),
        goal: goal.trim(),
        repos,
        constraints: lines,
        deadline: isoDeadline(deadline),
        submittedBy: session.actor,
      });
      // The files go up after the job exists, because an attachment is filed
      // against a job id, and before the plan is asked for, so the planning
      // agent is handed the screenshot rather than reading about it later. One
      // at a time and awaited: the hub takes one multipart body per call, and a
      // serial loop is what makes the failure below about a single named file.
      //
      // A failed upload is reported for what it is, the same way the plan
      // request below is: the job is already real, and saying "that did not
      // work" over an opening that did work would send a person to file it
      // again. The loop carries on, so one refused file does not strand the
      // ones after it.
      for (const file of files) {
        try {
          await api.uploadJobFile(filed.brief.id, file);
        } catch (failure) {
          toast.error(
            `Job opened, but ${file.name} did not upload: ${
              failure instanceof Error ? failure.message : 'unknown error'
            }. The other files are attached.`,
          );
        }
      }
      // Nothing decomposes itself: a job with no plan request sits in `draft`
      // until a person asks. Opening one and meaning to plan it is the same
      // intention, so the composer asks on your behalf. It is a second write,
      // and the job is already real after the first, so a failure here is
      // reported as what it is rather than as an opening that did not happen;
      // the job keeps its Request plan verb either way.
      try {
        await api.requestPlan(filed.brief.id);
        toast.success(`Job opened. ${PLAN_QUEUED_NOTE} You approve the plan before anything runs.`);
      } catch (failure) {
        toast.error(
          `Job opened, but asking for a plan failed: ${
            failure instanceof Error ? failure.message : 'unknown error'
          }. Use Request plan on the job.`,
        );
      }
      return filed;
    },
    {
      onDone: () => {
        closeCompose();
        setTitle('');
        setGoal('');
        setRepos([]);
        setFiles([]);
        setConstraints('');
        setDeadline('');
        setMoreOpen(false);
      },
      // The form stays open on a refusal and prints it under the buttons, where
      // the fields it is about still are.
      inlineError: true,
    },
  );

  useEffect(() => {
    if (composeOpen) first.current?.focus();
  }, [composeOpen]);

  return (
    <Modal open={composeOpen} onClose={closeCompose} label="New job" className="max-w-2xl">
      <div className="bg-sunken border-b px-gutter py-[calc(var(--dy-row-py)+1px)]">
        <div className="flex items-center gap-2">
          <h2 className="text-sm font-medium">New job</h2>
          <span className="text-fg-3 text-2xs">opened as {session.actor}</span>
          <Button
            variant="ghost"
            size="icon-xs"
            className="ml-auto"
            onClick={closeCompose}
            aria-label="Discard this job"
          >
            <X />
          </Button>
        </div>
        <p className="text-fg-2 mt-0.5 text-xs">
          Describe what you want done. A worker drafts a plan of tasks, you approve it, and each
          task comes back as a pull request for you to review.
        </p>
      </div>

      <form
        className="space-y-2.5 px-gutter py-3"
        onSubmit={(event) => {
          event.preventDefault();
          if (tooMuch || badFiles) return;
          open.run();
        }}
      >
        <div className="grid gap-2.5 sm:grid-cols-[minmax(0,1fr)_14rem]">
          <div className="space-y-1">
            <Label htmlFor="bf-title">Title</Label>
            <Input
              ref={first}
              id="bf-title"
              value={title}
              onChange={(event) => setTitle(event.target.value)}
              required
              minLength={3}
              maxLength={200}
              placeholder="Fix the flaky auth test"
              className="h-8 text-base"
            />
          </div>
          <div className="space-y-1">
            <Label htmlFor="bf-repos">Repo</Label>
            <Combobox
              id="bf-repos"
              values={repos}
              onChange={setRepos}
              options={known}
              required
              allowCustom={false}
              placeholder={known.length ? 'nymbl-app' : 'no repos registered yet'}
            />
            {known.length ? (
              <p className="text-fg-3 text-2xs">One or more, from the registry.</p>
            ) : (
              <p className="text-fg-3 text-2xs">
                {/*
                 * A plain anchor, not <Link>. The console tests render this
                 * composer on its own with a QueryClientProvider and no router,
                 * which is the convention every console test here follows, and
                 * a <Link> outside a RouterProvider throws and takes the whole
                 * component down. Client routes fall through to the SPA shell
                 * (packages/hub/src/web.ts), so the href resolves either way.
                 * The cost is one full page load on an empty-state hint an
                 * admin sees only before any repo is registered at all.
                 */}
                Nothing is registered yet.{' '}
                {session.isAdmin ? (
                  <a href="/admin/repos" className="underline underline-offset-2">
                    Register one
                  </a>
                ) : (
                  'Ask an admin to register one'
                )}
                .
              </p>
            )}
          </div>
        </div>

        <div className="space-y-1">
          <Label htmlFor="bf-goal">What do you want done?</Label>
          <Textarea
            id="bf-goal"
            value={goal}
            onChange={(event) => setGoal(event.target.value)}
            required
            minLength={10}
            rows={4}
            placeholder={
              'Describe the outcome, not the tasks. The worker reads the repo and drafts the tasks.\n\ne.g. The auth test passes ten runs in a row on CI, without a retry loop.'
            }
          />
        </div>

        {/* Always visible, not behind More options: attaching a screenshot of
            the thing that is wrong is the common case this field exists for,
            and a field nobody finds is a field nobody uses. */}
        <div className="space-y-1">
          <Label htmlFor="bf-files">Files to attach</Label>
          {/* A drop target around a real input, rather than a div pretending to
              be one: a pointer can drag onto the box, and a keyboard reaches
              the same picker by tabbing to the input the label names. */}
          {/* biome-ignore lint/a11y/noStaticElementInteractions: the rule guards
              against a static element being the only way to do something. Here
              the real file input inside is the control, reachable by tab and
              named by the label; these handlers only add pointer dragging on
              top of it, so giving the wrapper a role would announce a second
              control that does not exist. */}
          <div
            className="border-line-2 hover:border-accent-line rounded-md border border-dashed px-2.5 py-2 transition-colors duration-100"
            onDragOver={(event) => event.preventDefault()}
            onDrop={(event) => {
              event.preventDefault();
              addFiles(event.dataTransfer.files);
            }}
          >
            <input
              id="bf-files"
              type="file"
              multiple
              accept={FILE_TYPES.join(',')}
              onChange={(event) => {
                addFiles(event.target.files);
                // Cleared so picking the same file again, after removing it
                // from the list below, still counts as a change.
                event.target.value = '';
              }}
              className="text-fg-2 file:bg-raised file:text-fg-2 hover:file:text-foreground w-full text-xs file:mr-2 file:rounded-sm file:border-0 file:px-2 file:py-1 file:text-xs"
            />
            <p className="text-fg-3 mt-1 text-2xs">
              Or drop them here. Screenshots, mockups, a spec PDF: the agent is given these along
              with the goal. Up to {MAX_FILES} files, {formatBytes(MAX_FILE_BYTES)} each.
            </p>
          </div>
          {badFiles ? (
            <p className="text-bad text-2xs" role="alert">
              {badFiles}
            </p>
          ) : null}
          {files.length ? (
            <ul className="space-y-0.5 pt-0.5">
              {files.map((file) => (
                <li key={`${file.name}-${file.size}`} className="flex items-center gap-2 text-xs">
                  <Paperclip className="text-fg-3 size-3 shrink-0" aria-hidden />
                  <span className="min-w-0 truncate">{file.name}</span>
                  <span className="text-fg-3 tnum shrink-0 text-2xs">{formatBytes(file.size)}</span>
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-xs"
                    className="ml-auto"
                    onClick={() =>
                      setFiles((current) => current.filter((pending) => pending !== file))
                    }
                    aria-label={`Remove ${file.name}`}
                  >
                    <X />
                  </Button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>

        <button
          type="button"
          className="text-fg-2 hover:text-fg flex items-center gap-1 text-xs"
          onClick={() => setMoreOpen((current) => !current)}
          aria-expanded={showMore}
          aria-controls="bf-more"
        >
          {showMore ? <ChevronDown className="size-3.5" /> : <ChevronRight className="size-3.5" />}
          More options
          <span className="text-fg-3">rules, due date</span>
        </button>

        {showMore ? (
          <div id="bf-more" className="grid gap-2.5 sm:grid-cols-[minmax(0,1fr)_14rem]">
            <div className="space-y-1">
              <Label htmlFor="bf-constraints">Rules to follow</Label>
              <Textarea
                id="bf-constraints"
                value={constraints}
                onChange={(event) => setConstraints(event.target.value)}
                rows={2}
                placeholder={'No new dependencies\nKeep the public API stable'}
              />
              {tooMuch ? (
                <p className="text-bad text-2xs" role="alert">
                  {tooMuch}
                </p>
              ) : (
                <p className="text-fg-3 text-2xs">One per line. The planner is given these.</p>
              )}
            </div>
            <div className="space-y-1">
              <Label htmlFor="bf-deadline">Due date</Label>
              <Input
                id="bf-deadline"
                type="date"
                value={deadline}
                onChange={(event) => setDeadline(event.target.value)}
              />
              <p className="text-fg-3 text-2xs">End of that day.</p>
            </div>
          </div>
        ) : null}

        {open.error ? (
          <p className="text-bad text-xs" role="alert">
            {open.error}
          </p>
        ) : null}

        <div className="flex items-center gap-1.5 pt-0.5">
          <Button
            type="submit"
            size="sm"
            disabled={open.busy || Boolean(tooMuch) || Boolean(badFiles)}
            className="max-sm:h-9"
          >
            {open.busy ? 'Opening…' : 'Open job'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={closeCompose}
            className="max-sm:h-9"
          >
            Cancel
          </Button>
          <span className="text-fg-3 ml-auto hidden text-2xs sm:block">esc to close</span>
        </div>
      </form>
    </Modal>
  );
}

/** The trigger, for a view header. */
/**
 * File a job. The one verb every page in the console wants within reach.
 *
 * It used to be a 28px outlined button in the far right of a grey header bar,
 * on the reasoning that a permanent header control must not compete with the Go
 * button at an open gate. That reasoning held for the Inbox and cost every other
 * page its call to action: the thing a person came to the hub to do was the
 * quietest control on the screen.
 *
 * `tone="hero"` is the filled gradient pill, and it is what the dashboard hero
 * and the Jobs header draw. It carries the gradient in its own fill rather than
 * borrowing it from the surface under it, which is why it reads the same on the
 * hero and on a plain header bar. The gate list keeps the accent to itself by
 * staying a hairline the rest of the page, so the two never compete for the
 * same eye at the same weight.
 */
export function NewBriefButton({
  tone = 'quiet',
  label = 'New job',
}: {
  tone?: 'hero' | 'quiet';
  label?: string;
}) {
  const { openCompose } = useShell();
  return (
    <Button
      size={tone === 'hero' ? 'pill' : 'sm'}
      variant={tone === 'hero' ? 'hero' : 'outline'}
      onClick={openCompose}
      title="Open a new job · C"
    >
      <Plus aria-hidden />
      {label}
    </Button>
  );
}
